/**
 * SEO content pages, content-as-data. Imported by BOTH:
 *   - src/seo-pages/render.tsx        (React rendering)
 *   - scripts/seo/postbuild-seo.mjs   (per-page <head> + Article/FAQPage/BreadcrumbList
 *                                      JSON-LD + sitemap entries)
 * Plain JS so the Node build step can import it directly.
 *
 * Page metadata + the grounded comparison tables (as-of 2026-07-12, verified against the
 * scraped corpus at D:/01_PROJECTS/competitor-seo-corpus) live here; the PROSE (hero/intro/
 * sections/verdict/faq) is authored in ./prose.json by the grounded writing pass and spread
 * in per slug. See content.data.d.mts for the type.
 */
import PROSE from './prose.json' with { type: 'json' };

const CTA = { h2: 'See it on your own footage', sub: 'Free trial — 40 searches on your real library, no credit card. Windows + NVIDIA GPU.', button: 'Start free trial', href: '/trial' };

export const CONTENT_PAGES = [
  {
    slug: 'clipradar-vs-jumper',
    kind: 'compare',
    competitor: 'Jumper',
    title: 'ClipRadar vs Jumper: local AI footage search compared (2026)',
    description: 'ClipRadar vs Jumper — two local AI footage-search tools for editors. How they compare on search, faces, pricing, Premiere integration, and platforms. Honest, fact-checked.',
    datePublished: '2026-07-12',
    keywords: ['clipradar vs jumper', 'jumper alternative', 'jumper pro alternative', 'ai footage search'],
    comparison: {
      them: 'Jumper', asOf: 'July 2026',
      criteria: [
        { label: 'Search modes', us: 'Visual + speech + faces, fused by Ultra Search', usMark: 'yes', them: 'Visual + speech; faces & people search are Pro-only', themMark: 'part' },
        { label: 'Where analysis runs', us: '100% local on your GPU — nothing uploads', usMark: 'yes', them: 'Local too — offline', themMark: 'yes' },
        { label: 'Price (full features)', us: '$29/mo or $199 once — everything included', usMark: 'yes', them: '€169; faces + agentic need Pro (€599 once or €49/mo)', themMark: 'part' },
        { label: 'Premiere integration', us: 'Native docked panel', usMark: 'yes', them: 'Panel — one of four NLEs', themMark: 'yes' },
        { label: 'NLE breadth', us: 'Premiere-first (more coming)', usMark: 'part', them: 'Premiere, Final Cut, Resolve, Avid', themMark: 'yes' },
        { label: 'Platform', us: 'Windows + NVIDIA GPU', usMark: 'part', them: 'Windows + macOS', themMark: 'yes' },
        { label: 'Speech languages', us: '110+ languages', usMark: 'yes', them: '111 languages', themMark: 'yes' },
      ],
    },
    cta: CTA,
    related: [{ label: 'ClipRadar vs FrameQuery', to: '/compare/clipradar-vs-framequery' }, { label: 'ClipRadar vs WideFrame', to: '/compare/clipradar-vs-wideframe' }],
    ...PROSE['clipradar-vs-jumper'],
  },
  {
    slug: 'clipradar-vs-framequery',
    kind: 'compare',
    competitor: 'FrameQuery',
    title: 'ClipRadar vs FrameQuery: local vs cloud footage search (2026)',
    description: 'ClipRadar vs FrameQuery — one keeps analysis on your GPU, the other indexes in the cloud and meters by the hour. How they compare on privacy, price, and availability.',
    datePublished: '2026-07-12',
    keywords: ['clipradar vs framequery', 'framequery alternative', 'local vs cloud video search'],
    comparison: {
      them: 'FrameQuery', asOf: 'July 2026',
      criteria: [
        { label: 'Where analysis runs', us: '100% local on your GPU — nothing uploads', usMark: 'yes', them: 'Cloud-indexed — frames + audio go to cloud compute', themMark: 'no' },
        { label: 'Indexing cost', us: 'Unlimited hours, flat price', usMark: 'yes', them: 'Metered by video-hours ($19=10h, $45=50h, $190=300h)', themMark: 'no' },
        { label: 'Pricing', us: '$29/mo or $199 once', usMark: 'yes', them: '$19 / $45 / $190 per month, usage-based', themMark: 'part' },
        { label: 'Availability', us: 'Shipping — public beta', usMark: 'yes', them: '"Planned pricing, subject to change before launch"', themMark: 'part' },
        { label: 'Search modes', us: 'Visual + speech + faces, fused by Ultra Search', usMark: 'yes', them: 'Objects, transcription, scenes, faces & voice', themMark: 'yes' },
        { label: 'Premiere integration', us: 'Native docked panel', usMark: 'yes', them: 'Exports Premiere XML / FCPXML / EDL', themMark: 'part' },
        { label: 'Platform', us: 'Windows + NVIDIA GPU', usMark: 'yes', them: 'Windows', themMark: 'yes' },
      ],
    },
    cta: CTA,
    related: [{ label: 'ClipRadar vs Jumper', to: '/compare/clipradar-vs-jumper' }, { label: 'ClipRadar vs WideFrame', to: '/compare/clipradar-vs-wideframe' }],
    ...PROSE['clipradar-vs-framequery'],
  },
  {
    slug: 'clipradar-vs-wideframe',
    kind: 'compare',
    competitor: 'WideFrame',
    title: 'ClipRadar vs WideFrame: search-first vs auto-assembly (2026)',
    description: 'ClipRadar vs WideFrame — one finds the exact shot on Windows, the other auto-assembles a Premiere sequence on Mac. How they compare on platform, price, and control.',
    datePublished: '2026-07-12',
    keywords: ['clipradar vs wideframe', 'wideframe alternative', 'wideframe for windows'],
    comparison: {
      them: 'WideFrame', asOf: 'July 2026',
      criteria: [
        { label: 'Platform', us: 'Windows + NVIDIA GPU', usMark: 'yes', them: 'macOS only (Apple Silicon required)', themMark: 'no' },
        { label: 'Pricing', us: '$29/mo or $199 once', usMark: 'yes', them: '$100/mo', themMark: 'no' },
        { label: 'What it does', us: 'Search-first — you pick the exact shot', usMark: 'yes', them: 'Auto-assembly — describe it, get a .prproj', themMark: 'part' },
        { label: 'Control over the cut', us: 'You place every clip; agentic rough-cut optional', usMark: 'yes', them: 'AI assembles the sequence for you', themMark: 'part' },
        { label: 'Search modes', us: 'Visual + speech + faces, fused', usMark: 'yes', them: 'Semantic search feeding assembly', themMark: 'part' },
        { label: 'Premiere integration', us: 'Native docked panel', usMark: 'yes', them: 'Outputs native .prproj files', themMark: 'yes' },
      ],
    },
    cta: CTA,
    related: [{ label: 'ClipRadar vs Jumper', to: '/compare/clipradar-vs-jumper' }, { label: 'ClipRadar vs FrameQuery', to: '/compare/clipradar-vs-framequery' }],
    ...PROSE['clipradar-vs-wideframe'],
  },
  {
    slug: 'jumper-alternative',
    kind: 'compare',
    competitor: 'Jumper',
    title: 'The best Jumper alternative for Premiere editors (2026)',
    description: 'Looking for a Jumper alternative? ClipRadar is a local, Windows-first AI footage-search tool with faces, people search and Ultra Search on every plan — no Pro upsell.',
    datePublished: '2026-07-12',
    keywords: ['jumper alternative', 'alternative to jumper', 'jumper pro alternative', 'ai footage search'],
    comparison: {
      them: 'Jumper', asOf: 'July 2026',
      criteria: [
        { label: 'Faces & people search', us: 'Included on every plan', usMark: 'yes', them: 'Jumper Pro only', themMark: 'part' },
        { label: 'Pricing model', us: '$29/mo, $249/yr, or $199 once', usMark: 'yes', them: '€169 base; Pro €599 once or €49/mo', themMark: 'part' },
        { label: 'One-search fusion', us: 'Ultra Search blends visual + speech + faces', usMark: 'yes', them: 'Separate search modes', themMark: 'part' },
        { label: 'NLE breadth', us: 'Premiere-first', usMark: 'part', them: 'Premiere, Final Cut, Resolve, Avid', themMark: 'yes' },
        { label: 'Platform', us: 'Windows + NVIDIA GPU', usMark: 'part', them: 'Windows + macOS', themMark: 'yes' },
      ],
    },
    cta: CTA,
    related: [{ label: 'Full ClipRadar vs Jumper breakdown', to: '/compare/clipradar-vs-jumper' }],
    ...PROSE['jumper-alternative'],
  },
  {
    slug: 'search-footage-by-content-premiere-pro',
    kind: 'blog',
    title: 'How to search your footage by content in Premiere Pro',
    description: 'Stop scrubbing. Here is how to search a whole footage library by what is in the shot — visuals, spoken words, and faces — and drop the result straight onto your Premiere timeline.',
    datePublished: '2026-07-12',
    keywords: ['search footage by content', 'premiere pro footage search', 'find clips in premiere', 'ai footage search'],
    cta: CTA,
    related: [{ label: 'ClipRadar vs Jumper', to: '/compare/clipradar-vs-jumper' }],
    ...PROSE['search-footage-by-content-premiere-pro'],
  },
];
